import Team from '@/components/Team/Team'
import { useTheme } from '@/contexts/ThemeContext'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import { POSITION_CLASSES, POSITION_ANIMATIONS } from '@/utils/scorebugPosition'

import './TennisBug.css'

import type {
  JsonMatchData,
  TennisScore,
  TennisEventState,
  TennisCompletedSet,
  ScoreBugPosition,
} from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface TennisBugProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
  scorebugPosition?: ScoreBugPosition
}

const MULTIPLIERS = {
  setBoxSize: 0.85,
  setFontSize: 0.7,
  pointsBoxSize: 1.2,
  pointsFontSize: 0.85,
  serverDotSize: 0.18,
}

/**
 * Resolve the points slot for a side. In tiebreak / super-tiebreak the
 * tennis-style 0/15/30/40/AD is replaced by a raw integer count.
 */
function resolvePoints(
  side: 'home' | 'away',
  score: TennisScore | undefined,
  eventState: TennisEventState | undefined
): string {
  const cg = eventState?.currentGame
  if (cg?.isTiebreak || cg?.isSuperTiebreak) {
    const tb = cg.tiebreakPoints
    if (!tb) return '0'
    return String((side === 'home' ? tb.home : tb.away) ?? 0)
  }
  return String(score?.currentGamePoints ?? '0')
}

const TennisBug: FC<TennisBugProps> = ({ jsonData, isVisible = true, scorebugPosition = 'bottom-left' }) => {
  const theme = useTheme()
  const { in: animationType } = POSITION_ANIMATIONS[scorebugPosition]
  const animationRef = useTemplateAnimation(isVisible, { animationType, duration: 0.5 })

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)
  const homeScore = homeCompetitor?.score as TennisScore | undefined
  const awayScore = awayCompetitor?.score as TennisScore | undefined
  const eventState = jsonData?.eventState as TennisEventState | undefined

  const phase = eventState?.phase ?? 'PRE'
  const server = eventState?.server ?? 'home'
  const showLive = phase !== 'PRE'
  const positionClass = POSITION_CLASSES[scorebugPosition]

  // Build the columns to render. Two payload shapes exist in the wild:
  //   1. Pre-allocated: sets[] sized to match format (BO3 → 3 zero entries);
  //      sets[currentSetIndex] is the live set.
  //   2. Completed-only: sets[] holds finished sets; currentSetGames is live.
  // Either way, show completed sets followed by the live set as one column.
  // Future, unplayed sets are dropped. The rightmost column = the live set,
  // so no separate "games" box is rendered.
  const visibleSets = (sets: TennisCompletedSet[], currentSetGames: number): TennisCompletedSet[] => {
    const idx = eventState?.currentSetIndex
    if (typeof idx !== 'number') return sets
    if (!showLive) return sets.slice(0, idx)
    return sets[idx] ? sets.slice(0, idx + 1) : [...sets.slice(0, idx), { games: currentSetGames, tiebreak: null }]
  }

  // Pad sets[] of both sides to the same length so columns line up.
  const homeSets = visibleSets(homeScore?.sets ?? [], homeScore?.currentSetGames ?? 0)
  const awaySets = visibleSets(awayScore?.sets ?? [], awayScore?.currentSetGames ?? 0)
  const setCount = Math.max(homeSets.length, awaySets.length)

  return (
    <div className={`tennis-bug-content-wrapper ${positionClass}`}>
      <div ref={animationRef} className="tennis-bug-panel" data-scorebug-panel>
        <div className="tennis-bug-teams" style={{ background: hexToRgba(theme.colors.primary, 0.95) }}>
          <TennisRow
            competitor={homeCompetitor}
            isHome
            sets={homeSets}
            paddedSetCount={setCount}
            points={resolvePoints('home', homeScore, eventState)}
            isServing={server === 'home' && showLive}
            showLive={showLive}
            baseHeight={theme.baseHeight}
          />
          <TennisRow
            competitor={awayCompetitor}
            isHome={false}
            sets={awaySets}
            paddedSetCount={setCount}
            points={resolvePoints('away', awayScore, eventState)}
            isServing={server === 'away' && showLive}
            showLive={showLive}
            baseHeight={theme.baseHeight}
          />
        </div>

        <div
          className="tennis-bug-footer"
          style={{
            background: hexToRgba(theme.colors.secondary, 0.95),
            height: `calc(${theme.baseHeight} * 0.8)`,
          }}
        >
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            className="tennis-bug-branding-logo"
            style={{ height: `calc(${theme.baseHeight} * 0.4)` }}
          />
        </div>
      </div>
    </div>
  )
}

interface TennisRowProps {
  competitor: ReturnType<typeof getHomeCompetitor>
  isHome: boolean
  sets: TennisCompletedSet[]
  paddedSetCount: number
  points: string
  isServing: boolean
  showLive: boolean
  baseHeight: string
}

const TennisRow: FC<TennisRowProps> = ({
  competitor,
  isHome,
  sets,
  paddedSetCount,
  points,
  isServing,
  showLive,
  baseHeight,
}) => {
  return (
    <div className="tennis-bug-team-row">
      <div className="tennis-bug-name-cell" style={{ flex: 1, minWidth: 0 }}>
        <Team data={competitor} isHome={isHome} style={{ flex: 1, minWidth: 0 }} />
        {isServing && (
          <span
            className="tennis-bug-server-dot"
            aria-label="Serving"
            style={{
              width: `calc(${baseHeight} * ${MULTIPLIERS.serverDotSize})`,
              height: `calc(${baseHeight} * ${MULTIPLIERS.serverDotSize})`,
            }}
          />
        )}
      </div>

      {/* Completed-set columns — left-padded so home & away always align. */}
      {Array.from({ length: paddedSetCount }).map((_, i) => {
        const setData = sets[i]
        if (!setData) {
          return (
            <div
              key={i}
              className="tennis-bug-set-box tennis-bug-set-box-empty"
              style={{ width: `calc(${baseHeight} * ${MULTIPLIERS.setBoxSize})` }}
              aria-hidden="true"
            />
          )
        }
        return (
          <div
            key={i}
            className="tennis-bug-set-box"
            style={{ width: `calc(${baseHeight} * ${MULTIPLIERS.setBoxSize})` }}
          >
            <span
              className="tennis-bug-set-games"
              style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.setFontSize})` }}
            >
              {setData.games}
            </span>
            {setData.tiebreak !== null && (
              <sup
                className="tennis-bug-set-tiebreak"
                style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.setFontSize} * 0.55)` }}
              >
                {setData.tiebreak}
              </sup>
            )}
          </div>
        )
      })}

      {showLive && (
        <div className="tennis-bug-points-box" style={{ width: `calc(${baseHeight} * ${MULTIPLIERS.pointsBoxSize})` }}>
          <span
            className="tennis-bug-points-value"
            style={{ fontSize: `calc(${baseHeight} * ${MULTIPLIERS.pointsFontSize})` }}
          >
            {points}
          </span>
        </div>
      )}
    </div>
  )
}

export default TennisBug
